import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createBackup,
  getBackupDownloadUrl,
  listBackups,
  type BackupHealth,
} from "@/api/backups";
import { useToast } from "@/components/toast";

const QUERY_KEY = ["admin-backups"];

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("ru-RU");
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const cellStyle = {
  padding: "8px 10px",
  borderBottom: "1px solid var(--border-subtle, #e4e7ec)",
} as const;

const KIND_LABEL: Record<string, string> = {
  cron: "по расписанию",
  manual: "вручную",
  "pre-deploy": "pre-deploy",
};

const KIND_BADGE: Record<string, string> = {
  cron: "mt-badge--neutral",
  manual: "mt-badge--info",
  "pre-deploy": "mt-badge--warning",
};

/** Баннер свежести последнего регулярного бэкапа.
 *
 * Существует потому, что 2 и 3 августа 2026 ночные бэкапы падали на обоих
 * хостах, и это заметили только при написании документации: единственным
 * способом узнать было пойти и посмотреть журнал. Внешний канал (Telegram
 * по systemd OnFailure) заведён отдельно; здесь — чтобы возраст был виден
 * тому, кто и так открыл эту страницу.
 */
function HealthBanner({ health }: { health?: BackupHealth }) {
  if (!health || health.configured === false) return null;

  const age = health.age_hours ?? null;
  const max = health.max_age_hours ?? 30;
  // Жёлтый — уже подозрительно, но в пределах одного пропущенного прогона;
  // красный — прогонов нет больше суток сверх нормы.
  const level = health.healthy
    ? "ok"
    : age !== null && age <= max * 2
      ? "warn"
      : "bad";

  const palette = {
    ok: { bg: "var(--success-bg, #ecfdf3)", fg: "var(--success-fg, #027a48)" },
    warn: { bg: "var(--warning-bg, #fffaeb)", fg: "var(--warning-fg, #b54708)" },
    bad: { bg: "var(--error-bg, #fef3f2)", fg: "var(--error-fg, #b42318)" },
  }[level];

  const text = health.healthy
    ? `Последний регулярный бэкап снят ${age ?? "?"} ч назад — в норме (порог ${max} ч).`
    : health.reason || "Свежего регулярного бэкапа нет.";

  return (
    <div
      role={health.healthy ? undefined : "alert"}
      style={{
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.fg}33`,
        borderRadius: 6,
        padding: "10px 12px",
        marginBottom: "var(--sp-3)",
        fontSize: 14,
      }}
    >
      <strong>{health.healthy ? "✅ Бэкапы в порядке. " : "🔴 Внимание. "}</strong>
      {text}
    </div>
  );
}

export default function AdminBackups() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const r = await listBackups();
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const r = await createBackup();
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: (data) => {
      toast(`Бэкап создан: ${data.backup.filename}`, "success");
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) =>
      toast(`Бэкап не создан: ${err.message}`, "error"),
  });

  const downloadMutation = useMutation({
    mutationFn: async (key: string) => {
      const r = await getBackupDownloadUrl(key);
      if (!r.ok) throw new Error(r.error);
      return r.data.url;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err: Error) =>
      toast(`Не удалось скачать: ${err.message}`, "error"),
  });

  const data = query.data;
  const backups = data?.backups ?? [];

  return (
    <div style={{ padding: "var(--sp-3)", maxWidth: 1000 }}>
      <header style={{ marginBottom: "var(--sp-3)" }}>
        <h1>Бэкапы БД</h1>
        <p className="mt-text-tight mt-muted">
          Дамп PostgreSQL (<code>pg_dump</code>, custom-формат) на S3. Крон
          бэкапит автоматически; здесь можно запустить вручную, посмотреть
          историю и скачать. Восстановление —{" "}
          <code>pg_restore</code> из скачанного <code>.dump</code>.
        </p>
      </header>

      <HealthBanner health={data?.health} />

      <section className="rf-section" style={{ marginBottom: "var(--sp-3)" }}>
        <div className="rf-section__head">
          <span>Создать бэкап сейчас</span>
        </div>
        <div className="rf-section__body">
          <button
            type="button"
            className="mt-btn mt-btn--primary"
            disabled={createMutation.isPending || data?.configured === false}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending
              ? "Создаём дамп…"
              : "Создать бэкап"}
          </button>
          {data?.configured === false && (
            <span
              className="mt-text-tight mt-muted"
              style={{ marginLeft: 12 }}
            >
              S3 не настроен на этом инстансе (нет
              {" "}
              <code>MEDTRANSLATE_BACKUP_S3_*</code>).
            </span>
          )}
          {data?.configured !== false && (
            <span
              className="mt-text-tight mt-muted"
              style={{ marginLeft: 12 }}
            >
              Текущий инстанс: <strong>{data?.source ?? "—"}</strong>
            </span>
          )}
        </div>
      </section>

      <section className="rf-section">
        <div className="rf-section__head">
          <span>История бэкапов</span>
          <span className="mt-toolbar__sep" style={{ flex: 1 }} />
          <span className="mt-text-tight mt-muted">S3</span>
        </div>
        <div className="rf-section__body">
          {query.isLoading && <div className="mt-empty">Загружаем…</div>}
          {query.isError && (
            <div className="mt-empty mt-empty--error">
              {(query.error as Error | undefined)?.message ?? "Ошибка"}
            </div>
          )}
          {!query.isLoading && !query.isError && backups.length === 0 && (
            <div className="mt-empty">Бэкапов пока нет.</div>
          )}
          {backups.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Файл", "Тип", "Инстанс", "Снят", "Размер", ""].map((h) => (
                    <th
                      key={h}
                      className="mt-muted"
                      style={{
                        textAlign: "left",
                        padding: "8px 10px",
                        borderBottom:
                          "1px solid var(--border-default, #d0d5dd)",
                        fontSize: 12,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {backups.map((b) => (
                  <tr key={b.key}>
                    <td style={cellStyle}>
                      <span className="mt-mono" style={{ fontSize: 13 }}>
                        {b.filename}
                      </span>
                    </td>
                    <td style={cellStyle}>
                      <span
                        className={
                          "mt-badge mt-badge--no-dot " +
                          (KIND_BADGE[b.kind] ?? "mt-badge--neutral")
                        }
                      >
                        {KIND_LABEL[b.kind] ?? (b.kind || "—")}
                      </span>
                    </td>
                    <td style={cellStyle}>{b.source || "—"}</td>
                    {/* Дата СЪЁМКИ из имени файла. last_modified — только
                        запасной вариант: у объектов, перелитых между
                        хранилищами, это дата копирования, а не бэкапа. */}
                    <td style={cellStyle} title={b.key}>
                      {fmtDate(b.taken_at ?? b.last_modified)}
                    </td>
                    <td style={cellStyle}>{fmtSize(b.size)}</td>
                    <td style={cellStyle}>
                      <button
                        type="button"
                        className="mt-btn mt-btn--secondary mt-btn--sm"
                        disabled={downloadMutation.isPending}
                        onClick={() => downloadMutation.mutate(b.key)}
                      >
                        Скачать
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
